import { EvoluaApiError } from "@evolua/api-client";
import {
  Badge,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  Inline,
  PageHeader,
  Skeleton,
  Table,
  badgeToneForStatus,
} from "@evolua/ui";
import { useMemo, useState } from "react";
import { useMeTimecardBalance, useMeTimecards } from "../api/use-me.js";

function monthBounds(offsetMonths: number): { from: string; to: string; label: string } {
  const now = new Date();
  const start = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offsetMonths, 1),
  );
  const endExclusive = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offsetMonths + 1, 1),
  );
  const end = new Date(endExclusive.getTime() - 86_400_000);
  const today = new Date().toISOString().slice(0, 10);
  const from = start.toISOString().slice(0, 10);
  const toCandidate = end.toISOString().slice(0, 10);
  const to = toCandidate > today && offsetMonths === 0 ? today : toCandidate;
  const label = start.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return { from, to, label };
}

function formatPunch(at: string): string {
  try {
    return new Date(at).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return at;
  }
}

function formatBalance(minutes: number): string {
  const sign = minutes < 0 ? "-" : "";
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}h ${String(m).padStart(2, "0")}m`;
}

/** Consulta de ponto do colaborador (FASE-06-T37 / D-014). */
export function PontoPage() {
  const [monthOffset, setMonthOffset] = useState(0);
  const period = useMemo(() => monthBounds(monthOffset), [monthOffset]);
  const filters = useMemo(
    () => ({ from: period.from, to: period.to }),
    [period.from, period.to],
  );
  const list = useMeTimecards(filters);
  const balance = useMeTimecardBalance();

  const correlationId =
    list.error instanceof EvoluaApiError ? list.error.problem.traceId : undefined;

  if (list.isPending) {
    return <Skeleton height="8rem" label="Ponto" />;
  }

  if (list.isError || !list.data) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void list.refetch()}
      />
    );
  }

  if (!list.data.capabilityAvailable) {
    return (
      <>
        <PageHeader title="Ponto" />
        <EmptyState
          title="Módulo indisponível"
          description="O ERP deste cliente não disponibiliza o espelho de ponto. Quando o módulo for contratado, as marcações aparecem aqui."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Ponto"
        lead="Espelho de marcações e ocorrências como vieram do ERP — sem recálculo local."
      />

      <Inline gap={2}>
        <IconButton label="Mês anterior" onClick={() => setMonthOffset((v) => v - 1)}>
          <Icon name="chevron-left" size={18} />
        </IconButton>
        <strong>{period.label}</strong>
        <IconButton
          label="Próximo mês"
          disabled={monthOffset >= 0}
          onClick={() => setMonthOffset((v) => Math.min(0, v + 1))}
        >
          <Icon name="chevron-right" size={18} />
        </IconButton>
      </Inline>

      {balance.data?.capabilityAvailable ? (
        <p className="evolua-page-lead">
          Banco de horas:{" "}
          <Badge tone={balance.data.balanceMinutes >= 0 ? "success" : "warning"}>
            {formatBalance(balance.data.balanceMinutes)}
          </Badge>
        </p>
      ) : null}

      {list.data.data.length === 0 ? (
        <EmptyState
          title="Sem marcações no período"
          description="Não há espelho de ponto para as datas selecionadas."
        />
      ) : (
        <Table
          caption="Marcações"
          getRowId={(day) => day.id}
          columns={[
            {
              id: "date",
              header: "Dia",
              cell: (day) =>
                new Date(`${day.date}T12:00:00.000Z`).toLocaleDateString("pt-BR", {
                  weekday: "short",
                  day: "2-digit",
                  month: "2-digit",
                  timeZone: "UTC",
                }),
            },
            {
              id: "punches",
              header: "Marcações",
              cell: (day) => (
                <Inline className="evolua-punches">
                  {day.punches.map((p, idx) => (
                    <span key={`${day.id}-${idx}`}>
                      <Badge tone={badgeToneForStatus(p.type)}>{p.type}</Badge>{" "}
                      {formatPunch(p.at)}
                    </span>
                  ))}
                </Inline>
              ),
            },
            {
              id: "occurrences",
              header: "Ocorrências",
              cell: (day) =>
                day.occurrences.length > 0 ? (
                  <Inline>
                    {day.occurrences.map((o) => (
                      <Badge key={o.code} tone="warning">
                        {o.label}
                      </Badge>
                    ))}
                  </Inline>
                ) : (
                  "—"
                ),
            },
          ]}
          rows={list.data.data}
        />
      )}
    </>
  );
}
